import classnames from 'classnames'
import {
  BLANK_STRING,
  ButtonListFieldDirectionOption,
  ButtonListFieldOptions,
  FieldDataValue,
  FieldSelectOption,
} from 'embed/constants'
import { MultiProductState } from 'embed/reducers'
import { uiSelectedProductSelector } from 'embed/selectors/ui'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import { useTheme } from 'embed/hooks'

const CheckEmpty = require('embed/components/ApplicationField/check-empty.svg')
const CheckGreen = require('assets/components/check-in-filled-circle.svg')
const styles = require('./styles')

export interface ButtonListProps {
  onChange: (value: string) => void
  options: ButtonListFieldOptions
  value: FieldDataValue | undefined
  name: string
  forceFocus?: boolean
}

interface StoreProps {
  listOptions: FieldSelectOption[]
}

type Props = ButtonListProps & StoreProps

export const ButtonList: React.FC<Props> = (props) => {
  const { onChange, name, options, value, forceFocus } = props

  const buttonOptions = options.selectOptions
    ? options.selectOptions.filter((option) => option.value !== BLANK_STRING)
    : props.listOptions
  const direction = options.direction ?? ButtonListFieldDirectionOption.Column

  return (
    <div
      className={classnames(
        styles.buttonList,
        css`
          flex-direction: ${direction};
        `,
        { [styles.buttonListRow]: direction === ButtonListFieldDirectionOption.Row },
      )}
    >
      {buttonOptions.map((option, index) => (
        <ButtonListOption
          checked={value === option.value}
          key={option.value}
          index={index}
          label={option.label}
          name={name}
          onClick={onChange}
          value={option.value}
          direction={direction}
          forceFocus={forceFocus && index === 0}
        />
      ))}
    </div>
  )
}

export interface ButtonListOptionProps {
  checked: boolean
  index?: number
  label: string
  name: string
  onClick: (value?: React.ReactText) => void
  value?: React.ReactText
  direction?: ButtonListFieldDirectionOption
  forceFocus?: boolean
  multiSelect?: boolean
  centerAlign?: boolean
  hideEmptyCheck?: boolean
}

export const ButtonListOption: React.FC<ButtonListOptionProps> = ({
  centerAlign,
  checked,
  direction,
  forceFocus,
  hideEmptyCheck,
  index,
  label,
  multiSelect,
  name,
  onClick,
  value,
}) => {
  const onOptionClick = React.useCallback(() => {
    onClick(value)
  }, [onClick, value])

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        onOptionClick()
      }
    },
    [onOptionClick],
  )

  const inputProps = multiSelect
    ? {
        onClick: onOptionClick,
      }
    : {
        checked,
        onChange: onOptionClick,
      }
  let checkMark
  if (hideEmptyCheck) {
    checkMark = checked ? <CheckGreen className={styles.check} /> : <CheckEmpty className={styles.hiddenCheck} />
  } else if (centerAlign) {
    checkMark = checked ? <CheckGreen className={styles.check} /> : <CheckEmpty className={styles.check} />
  } else {
    checkMark = checked ? <CheckGreen className={styles.check} /> : null
  }

  const { borderRadiusSmall, colorBorder } = useTheme()

  return (
    <label
      className={classnames(
        styles.button,
        css`
          border-color: ${colorBorder} !important;
          border-radius: ${borderRadiusSmall} !important;
        `,
        { [styles.selected]: checked, [styles.rowButton]: direction === ButtonListFieldDirectionOption.Row },
      )}
      onKeyDown={onKeyDown}
      tabIndex={(index ?? 0) + 1}
    >
      <input className={styles.radio} name={name} type="radio" value={value} autoFocus={forceFocus} {...inputProps} />
      <span className={classnames(styles.buttonLabel, { [styles.centerAlign]: centerAlign })}>{label}</span>
      {checkMark}
    </label>
  )
}

export const mapStateToProps = (state: MultiProductState, ownProps: ButtonListProps): StoreProps => {
  const product = uiSelectedProductSelector(state) ?? ''
  const listOptions = (ownProps.options.pathToStore && state.filters[product]?.[ownProps.options.pathToStore]) ?? []

  return {
    listOptions,
  }
}

export default connect<StoreProps>(mapStateToProps, {})(ButtonList)
